import React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import JSDB from '../../../../fromKotlin/nk';
import {createDriverLink, PERM} from '../../../../api/customer/externalLinks.api';
import {OrderData} from '../../../../types/OrderData';

const DEFAULT_PERMISSIONS = PERM.SEE | PERM.DONE | PERM.ROUTE;

const PERMISSION_OPTIONS = [
    {bit: PERM.DONE, label: 'סימון בוצע והערות'},
    {bit: PERM.ROUTE, label: 'מיטוב ושינוי סדר המסלול'},
    {bit: PERM.EDIT, label: 'עריכת השדות והכמויות במסמך המוביל'},
    {bit: PERM.PRICES, label: 'צפייה במחירים'},
    {bit: PERM.SUMS, label: 'סיכום העמסה וכמויות כוללות'},
] as const;

export interface PersistentDriverLinkDialogProps {
    open: boolean;
    rows: OrderData[];
    onClose: () => void;
}

interface DriverOption {
    id: number;
    name: string;
    fromSelection: boolean;
}

function agentIsDriver(agent: any): boolean {
    try {
        if (typeof agent?.isDriver === 'function') return Boolean(agent.isDriver());
    } catch {
        // Kotlin/JS interop may expose the field only.
    }
    return Number(agent?.is_driver ?? 0) >= 1;
}

function resolveDriverFromRow(row: OrderData): DriverOption | null {
    const name = String(row?.orderDriver || row?.driverName || '').trim();
    const id = Number(row?.driverId);
    if (Number.isFinite(id) && id > 0) {
        const agent = JSDB().getAgentBy(id, null, false, true, false, false)?.first;
        return {
            id,
            name: String(agent?.user_name || name || `#${id}`).trim(),
            fromSelection: true,
        };
    }
    if (!name) return null;
    const agent = JSDB().getAgentBy(null, name, false, true, false, false)?.first;
    if (!agent?.id) return null;
    return {
        id: Number(agent.id),
        name: String(agent.user_name || name).trim(),
        fromSelection: true,
    };
}

function listCompanyDrivers(): DriverOption[] {
    try {
        const agents = JSDB().getAgents()?.first?.toArray?.() ?? [];
        return agents
            .filter((agent: any) => agentIsDriver(agent) && Number(agent?.id) > 0)
            .map((agent: any) => ({
                id: Number(agent.id),
                name: String(agent.user_name || `#${agent.id}`).trim(),
                fromSelection: false,
            }))
            .sort((a, b) => a.name.localeCompare(b.name, 'he'));
    } catch {
        return [];
    }
}

function buildDriverOptions(rows: OrderData[]): DriverOption[] {
    const byId = new Map<number, DriverOption>();
    rows.forEach((row) => {
        const driver = resolveDriverFromRow(row);
        if (!driver) return;
        const existing = byId.get(driver.id);
        if (!existing || (!existing.fromSelection && driver.fromSelection)) {
            byId.set(driver.id, driver);
        }
    });
    listCompanyDrivers().forEach((driver) => {
        if (!byId.has(driver.id)) byId.set(driver.id, driver);
    });
    return Array.from(byId.values()).sort((a, b) => {
        if (a.fromSelection !== b.fromSelection) return a.fromSelection ? -1 : 1;
        return a.name.localeCompare(b.name, 'he');
    });
}

const PersistentDriverLinkDialog = ({open, rows, onClose}: PersistentDriverLinkDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const drivers = React.useMemo(() => buildDriverOptions(rows), [rows]);
    const [driverId, setDriverId] = React.useState<number | ''>('');
    const [permissions, setPermissions] = React.useState(DEFAULT_PERMISSIONS);
    const [loading, setLoading] = React.useState(false);
    const [url, setUrl] = React.useState('');
    const [error, setError] = React.useState('');
    const [copied, setCopied] = React.useState(false);
    const linkInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const preferred = drivers.find((driver) => driver.fromSelection) ?? drivers[0];
        setDriverId(preferred?.id ?? '');
        setPermissions(DEFAULT_PERMISSIONS);
        setUrl('');
        setError('');
        setCopied(false);
    }, [open, drivers]);

    const canGenerate = driverId !== '' && Number(driverId) > 0 && !loading;

    const generate = async () => {
        if (!canGenerate) return;
        setLoading(true);
        setError('');
        try {
            const generatedUrl = await createDriverLink({
                mode: 'driver',
                perm: permissions | PERM.SEE,
                driverId: Number(driverId),
            });
            setUrl(generatedUrl);
        } catch (e: any) {
            setError(e?.message || 'יצירת הקישור נכשלה.');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            linkInputRef.current?.select();
            document.execCommand('copy');
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const selectedName = drivers.find((driver) => driver.id === Number(driverId))?.name;

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="sm"
            PaperProps={{dir: 'rtl'}}
        >
            <DialogTitle>יצירת קישור נהג קבוע</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Alert severity="info">
                        קישור קבוע לנהג — לא נסגר בסוף היום. הנהג בוחר תאריך ורואה את ההזמנות המשויכות אליו.
                        תוקף הקישור כ־180 יום.
                    </Alert>

                    {!drivers.length && (
                        <Alert severity="warning">לא נמצאו נהגים בחברה. הגדר נהג בלקוחות ואז צור קישור.</Alert>
                    )}

                    <TextField
                        select
                        fullWidth
                        label="נהג"
                        value={driverId}
                        onChange={(event) => {
                            setUrl('');
                            setDriverId(event.target.value === '' ? '' : Number(event.target.value));
                        }}
                        disabled={!drivers.length || loading}
                    >
                        {drivers.map((driver) => (
                            <MenuItem key={driver.id} value={driver.id}>
                                {driver.name}
                                {driver.fromSelection ? ' · מהמסומנים' : ''}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>הרשאות נהג</Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', columnGap: 2}}>
                            <FormControlLabel control={<Switch checked disabled />} label="צפייה" />
                            {PERMISSION_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.bit}
                                    control={(
                                        <Switch
                                            checked={Boolean(permissions & option.bit)}
                                            onChange={() => {
                                                setUrl('');
                                                setPermissions((current) => current & option.bit
                                                    ? current & ~option.bit
                                                    : current | option.bit);
                                            }}
                                            disabled={loading}
                                        />
                                    )}
                                    label={option.label}
                                />
                            ))}
                        </Box>
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}
                    {url && (
                        <Stack spacing={1}>
                            {selectedName && (
                                <Typography variant="body2" color="text.secondary">
                                    קישור קבוע עבור {selectedName}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    inputRef={linkInputRef}
                                    fullWidth
                                    size="small"
                                    value={url}
                                    InputProps={{readOnly: true}}
                                    inputProps={{dir: 'ltr'}}
                                    label="קישור נהג קבוע"
                                />
                                <Tooltip title={copied ? 'הועתק' : 'העתק'}>
                                    <IconButton onClick={copyLink} color={copied ? 'success' : 'default'}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="פתח">
                                    <IconButton onClick={() => window.open(url, '_blank')?.focus()}>
                                        <OpenInNewIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>סגור</Button>
                <Button
                    variant="contained"
                    onClick={generate}
                    disabled={!canGenerate}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {loading ? 'יוצר קישור…' : 'צור קישור קבוע'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PersistentDriverLinkDialog;
